/**
 * Cliente HTTP do Worker.
 * Auth via Bearer token em localStorage (cookie cross-domain bloqueado pelo Chrome).
 */

import type {
  Solicitante, Contrato, Demanda, Cota, Pessoa, Area, ItemKitCota, Evento, Episodio, Temporada, Inscricao,
  AuthenticatedUser, DashboardVisaoGeral,
  DetalhesEvento, PendenciaCliente, Lembrete, EntregaEscopo,
  Anotacao, RecursoTipo, AuditEntry, UploadedFile, TipoEntrega, CategoriaDemanda,
  AtivacaoMAC, LocalAtivacao, PlanejamentoEventoMAC, OfertaMAC,
  ExecucaoEvento,
} from './types';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';

export const tokenStore = {
  get(): string | null {
    try { return localStorage.getItem('apex_session'); } catch { return null; }
  },
  set(token: string): void {
    try { localStorage.setItem('apex_session', token); } catch { /* noop */ }
  },
  clear(): void {
    try { localStorage.removeItem('apex_session'); } catch { /* noop */ }
  },
};

class ApiError extends Error {
  constructor(public status: number, public body: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined ?? {}),
  };
  const tok = tokenStore.get();
  if (tok) headers['Authorization'] = `Bearer ${tok}`;

  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  });
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { body = await res.text(); }
    throw new ApiError(res.status, body, `${res.status} ${res.statusText}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

interface DataResponse<T> { data: T }

export const api = {
  auth: {
    me: () => request<{ user: AuthenticatedUser; token?: string }>('/auth/me'),
    magicLink: (email: string) =>
      request<{ ok: true; devMagicUrl?: string }>('/auth/magic-link', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
    verify: (token: string) =>
      request<{ token: string; user: AuthenticatedUser }>(
        `/auth/verify?token=${encodeURIComponent(token)}`,
      ),
    logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
    login: (usuario: string, senha: string) =>
      request<{ token: string; user: AuthenticatedUser }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ usuario, senha }),
      }),
  },
  demandas: {
    list: () => request<DataResponse<Demanda[]>>('/demandas'),
    get: (id: string) => request<DataResponse<Demanda>>(`/demandas/${id}`),
    create: (body: Partial<Demanda>) =>
      request<DataResponse<Demanda>>('/demandas', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (id: string, body: Partial<Demanda>) =>
      request<DataResponse<Demanda>>(`/demandas/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    delete: (id: string) =>
      request<{ id: string; deleted: boolean }>(`/demandas/${id}`, { method: 'DELETE' }),
    aprovar: (id: string, comentario?: string) =>
      request<DataResponse<Demanda>>(`/demandas/${id}/aprovar`, {
        method: 'POST',
        body: JSON.stringify({ comentario }),
      }),
    pedirRevisao: (id: string, feedback: string) =>
      request<DataResponse<Demanda>>(`/demandas/${id}/pedir-revisao`, {
        method: 'POST',
        body: JSON.stringify({ feedback }),
      }),
    comentar: (id: string, texto: string) =>
      request<DataResponse<Demanda>>(`/demandas/${id}/comentarios`, {
        method: 'POST',
        body: JSON.stringify({ texto }),
      }),
    atualizarDistribuicao: (id: string, body: { contratosIds?: string[]; clientesIds?: string[]; confirmada?: boolean }) =>
      request<DataResponse<Demanda>>(`/demandas/${id}/distribuicao`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    confirmarCreditos: (id: string, contratoIds: string[]) =>
      request<DataResponse<Demanda> & { confirmados: string[] }>(`/demandas/${id}/distribuicao/confirmar`, {
        method: 'POST',
        body: JSON.stringify({ contratoIds }),
      }),
    atualizarMetricas: (id: string, metricas: { views?: number; engajamento?: number }) =>
      request<DataResponse<Demanda>>(`/demandas/${id}/metricas`, {
        method: 'PATCH',
        body: JSON.stringify(metricas),
      }),
    reclassificar: (ids: string[], tipoEntrega: TipoEntrega) =>
      request<{ ok: boolean; total: number; success: number }>('/demandas/reclassificar', {
        method: 'POST',
        body: JSON.stringify({ ids, tipoEntrega }),
      }),
  },
  solicitantes: {
    list: () => request<DataResponse<Solicitante[]>>('/solicitantes'),
    create: (body: Partial<Solicitante>) =>
      request<DataResponse<Solicitante>>('/solicitantes', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    delete: (id: string) =>
      request<{ id: string; deleted: boolean; cascade: { demandas: number; contratos: number } }>(`/solicitantes/${id}`, { method: 'DELETE' }),
  },
  contratos: {
    list: () => request<DataResponse<Contrato[]>>('/contratos'),
    create: (body: Partial<Contrato> & { gerarDemandas?: boolean }) =>
      request<DataResponse<Contrato> & { demandasGeradas: number }>('/contratos', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    encerrar: (id: string) =>
      request<DataResponse<Contrato> & { solicitanteInativado: boolean }>(`/contratos/${id}/encerrar`, { method: 'PATCH' }),
  },
  cotas: { list: () => request<DataResponse<Cota[]>>('/cotas') },
  pessoas: {
    list: () => request<DataResponse<Pessoa[]>>('/pessoas'),
    update: (id: string, body: Partial<Pessoa>) =>
      request<DataResponse<Pessoa>>(`/pessoas/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },
  areas: { list: () => request<DataResponse<Area[]>>('/areas') },
  itensKit: { list: () => request<DataResponse<ItemKitCota[]>>('/itens-kit') },
  eventos: {
    list: () => request<DataResponse<Evento[]>>('/eventos'),
    listPortal: () => request<DataResponse<Evento[]>>('/eventos/portal'),
    create: (body: Partial<Evento>) =>
      request<DataResponse<Evento>>('/eventos', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    getDetalhes: (eventoId: string, solicitanteId?: string) => {
      const q = solicitanteId ? `?solicitanteId=${solicitanteId}` : '';
      return request<DataResponse<DetalhesEvento | null>>(`/eventos/${eventoId}/detalhes${q}`);
    },
    putDetalhes: (eventoId: string, body: Partial<DetalhesEvento>) =>
      request<DataResponse<DetalhesEvento>>(`/eventos/${eventoId}/detalhes`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    interesse: (eventoId: string, observacao?: string) =>
      request<DataResponse<Inscricao>>(`/eventos/${eventoId}/interesse`, {
        method: 'POST',
        body: JSON.stringify({ observacao }),
      }),
    confirmar: (eventoId: string, familia?: string) =>
      request<{ ok: boolean; eventoId: string; familia?: string; cardsGerados: number; cardsExistentes?: number; criados?: string[] }>(`/eventos/${eventoId}/confirmar`, {
        method: 'POST',
        body: JSON.stringify(familia ? { familia } : {}),
      }),
    execucao: (eventoId: string) =>
      request<{ data: ExecucaoEvento }>(`/eventos/${eventoId}/execucao`),
    painel: (eventoId: string) =>
      request<{ data: unknown }>(`/eventos/${eventoId}/painel`),
    checklistItem: (eventoId: string, demandaId: string, itemId: string, body: { estado: string; payload?: string }) =>
      request<{ ok: boolean }>(`/eventos/${eventoId}/checklist/${demandaId}/item/${itemId}`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },
  episodios: {
    list: () => request<DataResponse<Episodio[]>>('/episodios'),
    create: (body: Partial<Episodio>) =>
      request<DataResponse<Episodio>>('/episodios', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },
  temporadas: {
    list: () => request<DataResponse<Temporada[]>>('/temporadas'),
    /** cria a temporada e gera N episódios vinculados (modelo playlist). */
    create: (body: Partial<Temporada>) =>
      request<DataResponse<{ temporada: Temporada; episodios: Episodio[] }>>('/temporadas', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },
  detalhesEvento: {
    list: () => request<DataResponse<DetalhesEvento[]>>('/detalhes-evento'),
  },
  pendencias: {
    list: () => request<DataResponse<PendenciaCliente[]>>('/pendencias'),
    create: (body: Partial<PendenciaCliente>) =>
      request<DataResponse<PendenciaCliente>>('/pendencias', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (id: string, body: Partial<PendenciaCliente>) =>
      request<DataResponse<PendenciaCliente>>(`/pendencias/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },
  inscricoes: {
    list: () => request<DataResponse<Inscricao[]>>('/inscricoes'),
    create: (body: { eventoId: string; observacoes?: string }) =>
      request<DataResponse<Inscricao>>('/inscricoes', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (id: string, body: Partial<Inscricao>) =>
      request<DataResponse<Inscricao>>(`/inscricoes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },
  clientes: {
    convidar: (pessoaId: string, usuarioBase?: string) =>
      request<{ data: Pessoa; usuario: string; senha: string; emailEnviado: boolean }>(`/clientes/${pessoaId}/convidar`, {
        method: 'POST',
        body: JSON.stringify({ usuarioBase }),
      }),
  },
  lembretes: {
    list: () => request<DataResponse<Lembrete[]>>('/lembretes'),
    create: (body: Partial<Lembrete>) =>
      request<DataResponse<Lembrete>>('/lembretes', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (id: string, body: Partial<Lembrete>) =>
      request<DataResponse<Lembrete>>(`/lembretes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    delete: (id: string) =>
      request<{ id: string; deleted: boolean }>(`/lembretes/${id}`, { method: 'DELETE' }),
  },
  escopoEntregas: {
    list: (contratoId?: string) => {
      const q = contratoId ? `?contratoId=${contratoId}` : '';
      return request<DataResponse<EntregaEscopo[]>>(`/escopo-entregas${q}`);
    },
    create: (entregas: Partial<EntregaEscopo>[]) =>
      request<DataResponse<EntregaEscopo[]>>('/escopo-entregas', {
        method: 'POST',
        body: JSON.stringify({ entregas }),
      }),
    update: (id: string, body: Partial<EntregaEscopo>) =>
      request<DataResponse<EntregaEscopo>>(`/escopo-entregas/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    delete: (id: string) =>
      request<{ id: string; deleted: boolean }>(`/escopo-entregas/${id}`, { method: 'DELETE' }),
    aprovar: (id: string) =>
      request<DataResponse<EntregaEscopo>>(`/escopo-entregas/${id}/aprovar`, { method: 'POST' }),
    pedirRevisao: (id: string, feedback?: string) =>
      request<DataResponse<EntregaEscopo>>(`/escopo-entregas/${id}/revisao`, {
        method: 'POST',
        body: JSON.stringify({ feedback }),
      }),
  },
  dashboards: {
    visaoGeral: () => request<DataResponse<DashboardVisaoGeral>>('/dashboards/visao-geral'),
  },
  anotacoes: {
    list: (recursoTipo: RecursoTipo, recursoId: string) =>
      request<DataResponse<Anotacao[]>>(`/anotacoes?recursoTipo=${recursoTipo}&recursoId=${recursoId}`),
    create: (body: { texto: string; recursoTipo: RecursoTipo; recursoId: string; visivelCliente?: boolean }) =>
      request<DataResponse<Anotacao>>('/anotacoes', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (id: string, texto: string) =>
      request<DataResponse<Anotacao>>(`/anotacoes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ texto }),
      }),
    toggleVisibility: (id: string, visivelCliente: boolean) =>
      request<DataResponse<Anotacao>>(`/anotacoes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ visivelCliente }),
      }),
    delete: (id: string) =>
      request<{ id: string; deleted: boolean }>(`/anotacoes/${id}`, { method: 'DELETE' }),
  },
  audit: {
    list: (params?: { action?: string; usuario?: string; recursoTipo?: string; de?: string; ate?: string }) => {
      const q = new URLSearchParams();
      if (params?.action) q.set('action', params.action);
      if (params?.usuario) q.set('usuario', params.usuario);
      if (params?.recursoTipo) q.set('recursoTipo', params.recursoTipo);
      if (params?.de) q.set('de', params.de);
      if (params?.ate) q.set('ate', params.ate);
      const qs = q.toString();
      return request<DataResponse<AuditEntry[]>>(`/audit${qs ? `?${qs}` : ''}`);
    },
  },
  uploads: {
    list: (params: { demandaId?: string; contratoId?: string }) => {
      const q = params.demandaId ? `?demandaId=${params.demandaId}` : `?contratoId=${params.contratoId}`;
      return request<DataResponse<UploadedFile[]>>(`/uploads${q}`);
    },
    upload: async (file: File, opts: { demandaId?: string; contratoId?: string }) => {
      const fd = new FormData();
      fd.append('file', file);
      if (opts.demandaId) fd.append('demandaId', opts.demandaId);
      if (opts.contratoId) fd.append('contratoId', opts.contratoId);
      const tok = tokenStore.get();
      const headers: Record<string, string> = {};
      if (tok) headers['Authorization'] = `Bearer ${tok}`;
      const res = await fetch(`${API_URL}/uploads`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: fd,
      });
      if (!res.ok) {
        let body: unknown;
        try { body = await res.json(); } catch { body = await res.text(); }
        throw new ApiError(res.status, body, `${res.status} ${res.statusText}`);
      }
      return res.json() as Promise<DataResponse<UploadedFile>>;
    },
    delete: (key: string) =>
      request<{ deleted: boolean; key: string }>(`/uploads/${key}`, { method: 'DELETE' }),
  },
  notifRead: {
    get: () => request<{ ids: string[] }>('/me/notif-read'),
    save: (ids: string[]) => request<{ ok: boolean; ids: string[] }>('/me/notif-read', {
      method: 'PUT',
      body: JSON.stringify({ ids }),
    }),
  },
  // Sprint 8 — MAC
  mac: {
    ativacoes: {
      list: () => request<DataResponse<AtivacaoMAC[]>>('/mac/ativacoes'),
      create: (body: Partial<AtivacaoMAC>) =>
        request<DataResponse<AtivacaoMAC>>('/mac/ativacoes', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: Partial<AtivacaoMAC>) =>
        request<DataResponse<AtivacaoMAC>>(`/mac/ativacoes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      aprovar: (id: string) =>
        request<DataResponse<AtivacaoMAC> & { ok: boolean }>(`/mac/ativacoes/${id}/aprovar`, { method: 'POST', body: JSON.stringify({}) }),
      delete: (id: string) =>
        request<{ id: string; deleted: boolean }>(`/mac/ativacoes/${id}`, { method: 'DELETE' }),
    },
    locais: {
      list: () => request<DataResponse<LocalAtivacao[]>>('/mac/locais'),
      create: (body: Partial<LocalAtivacao>) =>
        request<DataResponse<LocalAtivacao>>('/mac/locais', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: Partial<LocalAtivacao>) =>
        request<DataResponse<LocalAtivacao>>(`/mac/locais/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    },
    planejamento: {
      list: () => request<DataResponse<PlanejamentoEventoMAC[]>>('/mac/planejamento'),
      get: (eventoId: string) => request<DataResponse<PlanejamentoEventoMAC | null>>(`/mac/planejamento/${eventoId}`),
      put: (eventoId: string, body: Partial<PlanejamentoEventoMAC> & { itens?: { ativacaoId: string; canalIds: string[] }[] }) =>
        request<DataResponse<PlanejamentoEventoMAC>>(`/mac/planejamento/${eventoId}`, { method: 'PUT', body: JSON.stringify(body) }),
      confirmar: (eventoId: string) =>
        request<{ ok: boolean; eventoId: string; cardsGerados: number; criados?: string[]; enxovalConcluido?: boolean }>(`/mac/planejamento/${eventoId}/confirmar`, { method: 'POST', body: JSON.stringify({}) }),
      delete: (eventoId: string) =>
        request<{ id: string; deleted: boolean }>(`/mac/planejamento/${eventoId}`, { method: 'DELETE' }),
    },
    ofertas: {
      list: () => request<DataResponse<OfertaMAC[]>>('/mac/ofertas'),
      create: (body: Partial<OfertaMAC>) =>
        request<DataResponse<OfertaMAC>>('/mac/ofertas', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: Partial<OfertaMAC>) =>
        request<DataResponse<OfertaMAC>>(`/mac/ofertas/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      delete: (id: string) =>
        request<{ id: string; deleted: boolean }>(`/mac/ofertas/${id}`, { method: 'DELETE' }),
    },
  },
};

export { ApiError };
